'use client'

import Script from 'next/script'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { combineLatest, of } from 'rxjs'
import { defaultIfEmpty } from 'rxjs/operators'

import { isBrowser } from '../../../../lib/utils'
import { asyncGlobalVariable } from '../../../../lib/utils/asyncGlobalVariable'
import {
  AmplitudeEvent,
  amplitudeSessionReplayFlagKey,
  convertExperimentationContextToAmplitudeIdentity,
  getAmplitudeFlagDecisionAnalytics,
  getSessionReplaySampleRate,
  sessionReplayPrivacyConfig,
} from '../../../_config/Amplitude.config'
import { useConsentPolicy } from '../../../_providers/ConsentPolicyProvider.client'
import {
  useExperimentationContext,
  useFeatureFlag,
} from '../../../_providers/ExperimentationProvider.client'
import { useSession } from '../../../_providers/SessionProvider.client'
import { useUser } from '../../../_providers/UserProvider.client'

const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY

// Just check for a function that doesn't get stubbed but exists after loading finishes. So if it exists, we are fully live.
const isAmplitudeReady$ = asyncGlobalVariable(() => {
  if (typeof globalThis.amplitude?.getOptOut === 'undefined') {
    return undefined
  } else {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return Boolean(globalThis.amplitude?.getOptOut)
  }
})

// Session Replay ships as a separate CDN bundle with no stub, so unlike `amplitude` its
// global is simply absent until the script lands. Kept short because Amplitude init waits
// on this: see the `defaultIfEmpty` note in `AmplitudeIdentitySync`.
const SESSION_REPLAY_LOAD_TIMEOUT_MS = 3000
const sessionReplayGlobal$ = asyncGlobalVariable(
  () => {
    if (typeof globalThis.sessionReplay?.sessionReplayPlugin === 'undefined') {
      return undefined
    }
    return globalThis.sessionReplay
  },
  { pollTimeout: SESSION_REPLAY_LOAD_TIMEOUT_MS }
)

export const useAmplitudeIdentity = () => {
  const { consentPolicy } = useConsentPolicy()
  const { user } = useUser()
  const { attributes } = useSession()
  const experimentationContext = useExperimentationContext()
  const identity = useMemo(() => {
    return convertExperimentationContextToAmplitudeIdentity({
      session: attributes,
      accountId: user?.id,
      experimentationContext,
      consentPolicy,
    })
  }, [attributes, consentPolicy, experimentationContext, user?.id])
  return {
    identity,
  }
}

const AmplitudeIdentitySync = ({
  sessionReplayEnabled,
  sessionReplaySampleRate,
}: {
  sessionReplayEnabled: boolean
  sessionReplaySampleRate: number
}) => {
  const { attributes } = useSession()
  const { identity } = useAmplitudeIdentity()

  const logLevel = useMemo(() => {
    const logLevel = process.env.NEXT_PUBLIC_AMPLITUDE_LOG_LEVEL
      ? +process.env.NEXT_PUBLIC_AMPLITUDE_LOG_LEVEL
      : 1
    // Use -1 log level on client as server for prod.
    return process.env.NEXT_PUBLIC_FIGS_ENV === 'prod' ? logLevel - 1 : logLevel
  }, [])

  const initHasRunRef = useRef(false)
  useEffect(() => {
    // Do nothing on server browser
    if (!isBrowser() || !apiKey) {
      return
    }

    // If we have already initialized, we just update with new identity
    if (initHasRunRef.current) {
      globalThis.amplitude?.identify?.({
        getUserProperties: () => {
          return {
            $set: identity,
          }
        },
      })
      return
    }

    // If we are not initialized, we wait until we can, then we do so.
    const observableSubscription = combineLatest([
      isAmplitudeReady$,
      // Session Replay lives in a separate CDN bundle, so its global may never arrive (blocked
      // request, CDN outage, adblocker). `asyncGlobalVariable` completes *silently* on timeout
      // rather than emitting, so without `defaultIfEmpty` a missing plugin would mean `init`
      // never runs and all Amplitude analytics would quietly stop. This makes the failure mode
      // "initialize without replay" instead, at the cost of delaying init by at most
      // SESSION_REPLAY_LOAD_TIMEOUT_MS when the script is unreachable.
      sessionReplayEnabled ? sessionReplayGlobal$.pipe(defaultIfEmpty(undefined)) : of(undefined),
    ]).subscribe(([, sessionReplay]) => {
      // Plugins must be registered before `init` so tracked events carry the replay id that
      // links them to their recording.
      if (sessionReplay?.sessionReplayPlugin) {
        globalThis.amplitude?.add?.(
          sessionReplay.sessionReplayPlugin({
            sampleRate: sessionReplaySampleRate,
            privacyConfig: sessionReplayPrivacyConfig,
          })
        )
      }

      globalThis.amplitude?.init?.(apiKey, attributes.visitorId, {
        appVersion: process.env.NEXT_PUBLIC_MAGNOLIA_VERSION ?? '0.0.0',
        logLevel,
        autocapture: {}, // autocapture = empty object means defaults
        identify: {
          getUserProperties: () => {
            return {
              $set: identity,
            }
          },
        },
      })
      initHasRunRef.current = true
    })
    return () => {
      observableSubscription.unsubscribe()
    }
  }, [attributes.visitorId, identity, logLevel, sessionReplayEnabled, sessionReplaySampleRate])

  return null
}

// NOTE: If updating this, ensure it does not stub `getOptOut` since we use that above to check if Amplitude has loaded
// Also ensure it does not inject a script tag since `AmplitudeExternalScript` handles that instead of this. This is just to stub out the browser object.
export const AmplitudeStub = () => {
  // Supress React 19/Next 16 errors related to executing `<script />` on the client
  // The script tag will still be rendered on the server & will persist once served to the client if rendered in the `head`.
  if (typeof window !== 'undefined') return null

  return (
    <script
      id='amplitude-initialize'
      dangerouslySetInnerHTML={{
        __html: `
        !function(){"use strict";!function(e,t){var r=e.amplitude||{_q:[],_iq:{}};if(!r.invoked){var n=function(e,t){e.prototype[t]=function(){return this._q.push({name:t,args:Array.prototype.slice.call(arguments,0)}),this}},s=function(e,t,r){return function(n){e._q.push({name:t,args:Array.prototype.slice.call(r,0),resolve:n})}},o=function(e,t,r){e[t]=function(){if(r)return{promise:new Promise(s(e,t,Array.prototype.slice.call(arguments)))};!function(e,t,r){e._q.push({name:t,args:Array.prototype.slice.call(r,0)})}(e,t,Array.prototype.slice.call(arguments))}},i=function(e){for(var t=0;t<y.length;t++)o(e,y[t],!1);for(var r=0;r<g.length;r++)o(e,g[r],!0)};r.invoked=!0;for(var u=function(){return this._q=[],this},p=["add","append","clearAll","prepend","set","setOnce","unset","preInsert","postInsert","remove","getUserProperties"],l=0;l<p.length;l++)n(u,p[l]);r.Identify=u;for(var d=function(){return this._q=[],this},f=["getEventProperties","setProductId","setQuantity","setPrice","setRevenue","setRevenueType","setReceipt","setReceiptSig","setCurrency","setEventProperties"],v=0;v<f.length;v++)n(d,f[v]);r.Revenue=d;var y=["getDeviceId","setDeviceId","getSessionId","setSessionId","getUserId","setUserId","setOptOut","setTransport","reset","extendSession"],g=["init","add","remove","track","logEvent","identify","groupIdentify","setGroup","revenue","flush"];i(r),r.createInstance=function(e){return r._iq[e]={_q:[]},i(r._iq[e]),r._iq[e]},e.amplitude=r}}(window,document)}();
      `,
      }}
    />
  )
}

const AmplitudeExternalScript = () => {
  const {
    consentPolicy: { performance },
  } = useConsentPolicy()

  if (!apiKey || performance !== 'allowed') return null

  return (
    <Script
      id='amplitude-source'
      crossOrigin='anonymous'
      integrity='sha384-g+r7BBBM1JF5/SzYa399CX1OrUUtCetIQY7M9EyA4bE7PGsw1/2NAoEMu8ASuB0c'
      strategy='afterInteractive' // Analytics is set to afterInteractive
      src='https://cdn.amplitude.com/libs/analytics-browser-2.36.3-min.js.gz'
    />
  )
}

// Session Replay bundle. The `integrity` hash is computed over the DECODED body, since the
// CDN serves these `.js.gz` files with `Content-Encoding: gzip`:
//   curl -s --compressed -o f.js <url> && openssl dgst -sha384 -binary f.js | openssl base64 -A
// Hashing the raw `.gz` bytes yields a hash the browser rejects. Keep the version in sync
// with `src/types/amplitude-session-replay.ts`.
const AmplitudeSessionReplayScript = () => {
  return (
    <Script
      id='amplitude-session-replay-source'
      crossOrigin='anonymous'
      integrity='sha384-fDBGhU3cbeaModrmyxMZzeb5OT8eKkFhljS//u0vGiLh9kQwSUeQEBcRvCrLgeDA'
      strategy='afterInteractive' // Analytics is set to afterInteractive
      src='https://cdn.amplitude.com/libs/plugin-session-replay-browser-1.35.1-min.js.gz'
    />
  )
}

/**
 * Decides whether this pageview records a session replay, then loads the plugin and hands
 * the decision to `AmplitudeIdentitySync` so it can register the plugin before `init`.
 *
 * Replay is opt-in per layout (see the `sessionReplay` prop on `AmplitudeScript`) so that
 * analytics coverage and replay coverage can differ.
 */
const AmplitudeSessionReplaySync = ({ sessionReplay }: { sessionReplay: boolean }) => {
  const {
    consentPolicy: { performance },
  } = useConsentPolicy()
  const sampleRate = useMemo(() => getSessionReplaySampleRate(), [])

  // `lock-pageview-to-controlVariant` keeps the decision stable for the pageview, which
  // matters because the plugin can only be registered before `init`. Flags are bootstrapped
  // server side, so a real decision is available on first render; if LaunchDarkly is
  // unavailable this locks to 'false', which is the correct direction to fail.
  const sessionReplayDecision = useFeatureFlag<'true' | 'false'>({
    key: amplitudeSessionReplayFlagKey,
    controlVariant: 'false',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    skip: !sessionReplay || performance !== 'allowed' || sampleRate <= 0,
  })

  const sessionReplayEnabled =
    sessionReplay && performance === 'allowed' && sampleRate > 0 && sessionReplayDecision === 'true'

  return (
    <>
      {sessionReplayEnabled ? <AmplitudeSessionReplayScript /> : null}
      <AmplitudeIdentitySync
        sessionReplayEnabled={sessionReplayEnabled}
        sessionReplaySampleRate={sampleRate}
      />
    </>
  )
}

export const AmplitudeScript = ({
  /**
   * Opt this layout into Session Replay. Off by default so layouts covering PII-dense flows
   * (B2B onboarding, gift claim) keep analytics without being recorded.
   */
  sessionReplay = false,
}: {
  sessionReplay?: boolean
} = {}) => {
  if (!apiKey) {
    globalThis.amplitude = undefined
    return null
  }

  return (
    <>
      <AmplitudeExternalScript />
      <AmplitudeSessionReplaySync sessionReplay={sessionReplay} />
    </>
  )
}

export const useAmplitude = () => {
  const track = useCallback(({ eventName, eventId, eventProperties }: AmplitudeEvent) => {
    globalThis.amplitude?.track?.(eventName, eventProperties, {
      insert_id: eventId, // Prevents duplicates from showing in Amplitude.
    })
  }, [])

  const sendFeatureFlagDecision = useCallback(
    (flagKey: string, flagDecision: string) => {
      const analyticsData = getAmplitudeFlagDecisionAnalytics(flagKey, flagDecision)
      globalThis.amplitude?.identify?.({
        getUserProperties: () => {
          return {
            $postInsert: analyticsData.userProperties,
          }
        },
      })
      track(analyticsData)
    },
    [track]
  )

  return useMemo(() => {
    return {
      track,
      sendFeatureFlagDecision,
    }
  }, [track, sendFeatureFlagDecision])
}
